import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';

/**
 * SEO-optimized landing page targeting "accelerated professionals" keyword
 * Designed to rank #1 for accelerated professionals searches
 */
const AcceleratedProfessionalsPage = () => {
  // SEO configuration for maximum keyword targeting
  const seoConfig = {
    title: 'Accelerated Professionals | The APN Network | Join Elite Professionals',
    description: 'Join accelerated professionals at The APN - the premier network for accelerated professionals seeking career growth. Connect with accelerated professionals worldwide in finance, consulting, and business.',
    keywords: [
      'accelerated professionals',
      'accelerated professional network',
      'accelerated professionals network',
      'accelerated career professionals',
      'elite accelerated professionals',
      'accelerated business professionals',
      'accelerated finance professionals',
      'accelerated consulting professionals',
      'the apn accelerated professionals',
      'join accelerated professionals',
      'accelerated professionals community',
      'accelerated professionals platform'
    ],
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      'name': 'Accelerated Professionals Network',
      'description': 'The premier network for accelerated professionals seeking career advancement and professional growth.',
      'url': 'https://the-apn.web.app/accelerated-professionals',
      'mainEntity': {
        '@type': 'Organization',
        'name': 'The APN - Accelerated Professionals Network',
        'description': 'Connecting accelerated professionals worldwide for career growth and networking opportunities.'
      }
    }
  };

  useSEO(seoConfig);

  return (
    <>
      <Helmet>
        <title>Accelerated Professionals | The APN Network | Join Elite Professionals</title>
        <meta name="description" content="Join accelerated professionals at The APN - the premier network for accelerated professionals seeking career growth. Connect with accelerated professionals worldwide in finance, consulting, and business." />
        <meta name="keywords" content="accelerated professionals, accelerated professional network, accelerated professionals network, elite professionals, career acceleration" />
        <link rel="canonical" href="https://the-apn.web.app/accelerated-professionals" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
        {/* Hero Section - Accelerated Professionals Focus */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              <span className="text-blue-600">Accelerated Professionals</span>
              <br />
              Network
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto">
              Join the premier network of <strong>accelerated professionals</strong> who are transforming their careers 
              and achieving extraordinary success. Connect with <strong>accelerated professionals</strong> worldwide.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Join Accelerated Professionals
              </Link>
              <Link
                to="/about/our-story"
                className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                Learn About The APN
              </Link>
            </div>
          </div>
        </section>

        {/* What Makes Professionals Accelerated */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
              What Makes <span className="text-blue-600">Accelerated Professionals</span> Different?
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3">Rapid Career Growth</h3>
                <p className="text-gray-600">
                  <strong>Accelerated professionals</strong> leverage strategic networking and exclusive opportunities 
                  to fast-track their career advancement.
                </p>
              </div>
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3">Elite Network Access</h3>
                <p className="text-gray-600">
                  Connect with other <strong>accelerated professionals</strong> in finance, consulting, 
                  and business leadership roles.
                </p>
              </div>
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3">Proven Success</h3>
                <p className="text-gray-600">
                  Join thousands of <strong>accelerated professionals</strong> who have achieved 
                  remarkable career milestones through The APN.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Accelerated Professionals Success Stories */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
              <span className="text-blue-600">Accelerated Professionals</span> Success Stories
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <p className="text-gray-600 mb-4 italic">
                  "Joining The APN transformed my career trajectory. As an <strong>accelerated professional</strong>, 
                  I connected with industry leaders and landed my dream role in investment banking."
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <span className="text-blue-600 font-semibold">JS</span>
                  </div>
                  <div>
                    <h4 className="font-semibold">John Smith</h4>
                    <p className="text-gray-500">VP, Goldman Sachs</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <p className="text-gray-600 mb-4 italic">
                  "The APN's network of <strong>accelerated professionals</strong> opened doors I never knew existed. 
                  The quality of connections and opportunities is unmatched."
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <span className="text-blue-600 font-semibold">MJ</span>
                  </div>
                  <div>
                    <h4 className="font-semibold">Maria Johnson</h4>
                    <p className="text-gray-500">Partner, McKinsey & Company</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Join Accelerated Professionals CTA */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-600">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Become an <span className="text-blue-200">Accelerated Professional</span>?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Join thousands of <strong>accelerated professionals</strong> who are advancing their careers 
              through The APN's exclusive network and opportunities.
            </p>
            <Link
              to="/register"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
            >
              Join The Accelerated Professionals Network
            </Link>
          </div>
        </section>

        {/* SEO Footer Content */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
          <div className="max-w-6xl mx-auto">
            <h3 className="text-2xl font-bold mb-6 text-center">
              The Premier <span className="text-blue-400">Accelerated Professionals</span> Network
            </h3>
            <div className="grid md:grid-cols-3 gap-8 text-sm">
              <div>
                <h4 className="font-semibold mb-3">For Accelerated Professionals</h4>
                <ul className="space-y-2 text-gray-300">
                  <li>• Connect with accelerated professionals worldwide</li>
                  <li>• Access exclusive career opportunities</li>
                  <li>• Join accelerated professionals events</li>
                  <li>• Build your accelerated professional brand</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-3">Accelerated Professional Benefits</h4>
                <ul className="space-y-2 text-gray-300">
                  <li>• Accelerated career advancement</li>
                  <li>• Elite accelerated professionals network</li>
                  <li>• Accelerated professional development</li>
                  <li>• Accelerated professionals mentorship</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-3">Join Accelerated Professionals</h4>
                <ul className="space-y-2 text-gray-300">
                  <li>• Apply for accelerated professionals membership</li>
                  <li>• Meet other accelerated professionals</li>
                  <li>• Accelerated professionals directory access</li>
                  <li>• Accelerated professionals success support</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AcceleratedProfessionalsPage;
